<template>
	<CardTable :title="title">
		<!-- Desktop Table View -->
		<div class="hidden lg:block overflow-x-auto">
			<table class="min-w-full divide-y divide-slate-200/50 dark:divide-slate-700/50">
				<!-- Header -->
				<thead class="bg-gradient-to-r from-primary/5 via-primary/3 to-transparent dark:from-primary-dark/10 dark:via-primary-dark/5 dark:to-transparent">
					<tr>
						<th class="px-6 py-4 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider w-[40%]">File Name</th>
						<th class="px-6 py-4 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider w-[12%]">Type</th>
						<th class="px-6 py-4 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider w-[22%]">Viewers</th>
						<th class="px-6 py-4 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider w-[10%] whitespace-nowrap">Date Added</th>
						<th class="px-6 py-4 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider w-[8%]">Status</th>
						<th class="px-6 py-4 text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider w-[8%]">Quick Actions</th>
					</tr>
				</thead>

				<!-- Body -->
				<tbody class="bg-white dark:bg-slate-900/50 divide-y divide-slate-200/50 dark:divide-slate-700/50">
					<!-- Error State -->
					<tr v-if="error">
						<td colspan="6" class="px-6 py-12 text-center text-red-600 dark:text-red-400">
							{{ error }}
						</td>
					</tr>
                    
                    <!-- <TableLoader v-else-if="loading" :columns="6" /> -->
					<TableLoader v-else-if="loading" :colspan="6" title="Fetching Material"/>

					<!-- Empty State -->
					<tr v-else-if="materials.length === 0">
						<td colspan="6">
							<div class="text-center py-16 px-4">
								<div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 mb-4">
									<FileText class="w-8 h-8 text-slate-400 dark:text-slate-500" />
								</div>
								<p class="text-base font-medium text-slate-600 dark:text-slate-300">No records found</p>
								<p v-if="totalUnfiltered && totalUnfiltered > 0" class="text-sm text-slate-500 dark:text-slate-400 mt-1">Try adjusting your search or filters</p>
							</div>
						</td>
					</tr>

					<!-- Data Rows -->
					<tr v-else v-for="m in materials" :key="m.id" class="hover:bg-primary/5 dark:hover:bg-primary-dark/5 transition-all duration-300 ease-out group">
						<td class="px-6 py-4 text-sm font-semibold text-slate-700 dark:text-slate-300">{{ m.title }}</td>
						<td class="px-6 py-4 max-w-xs truncate">
							<span class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg"
								:class="m.type === 'Doc File' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400' : m.type === 'Online form' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400' : m.type === 'URL Link' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400' : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'"
							>
								{{ m.type }}
							</span>
						</td>
						<td class="px-6 py-4 text-sm text-slate-500 dark:text-slate-400 max-w-xs">
							<div class="flex items-center gap-2">
								<span class="truncate">
									<span v-for="(viewer, index) in m.position_viewers" :key="'pos-'+viewer.id">
								  {{ viewer.name }}{{ index < (m.position_viewers?.length || 0) - 1 || (m.user_viewers?.length || 0) > 0 ? ', ' : '' }}
								</span>
									<span v-for="(viewer, index) in m.user_viewers" :key="'usr-'+viewer.id">
								  {{ viewer.first_name ? viewer.first_name + ' ' + viewer.last_name : viewer.username }}{{ index < (m.user_viewers?.length || 0) - 1 ? ', ' : '' }}
								</span>
									<span v-if="(!m.position_viewers || m.position_viewers.length === 0) && (!m.user_viewers || m.user_viewers.length === 0)" class="text-slate-400 italic">None</span>
								</span>
								<button
									type="button"
									@click.stop="$emit('editViewers', m)"
									class="flex-shrink-0 p-1 rounded-lg text-slate-400 hover:text-primary hover:bg-primary/10 dark:hover:bg-primary-dark/10 transition-all"
									title="Edit Viewers"
								>
									<UserPen class="h-4 w-4" />
								</button>
							</div>
						</td>
						<td class="px-6 py-4 text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
							{{ m.created_at ? new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A' }}
						</td>
						<td class="px-6 py-4">
							<span
							class="badge"
							:class="m.status === 'active' ? 'badge-success' : 'badge-danger'"
							>
							{{ m.status === 'active' ? 'Active' : 'Inactive' }}
							</span>
						</td>
						<td class="px-6 py-4">
							<div class="flex gap-2 justify-center">
							<IconButton
								title="View"
								variant="neutral"
								@click="$emit('view', m)"
							>
								<ViewIcon class="h-4 w-4 xl:h-5 xl:w-5 text-blue-600" />
							</IconButton>

							<IconButton
								v-if="m.type === 'Doc File' && m.file_path"
								title="Download"
								variant="neutral"
								@click="$emit('download', m)"
							>
								<DownloadIcon class="h-4 w-4 xl:h-5 xl:w-5" />
							</IconButton>

                            <IconButton
								v-if="m.type === 'Doc File' && m.file_path"
								title="Print"
								variant="neutral"
								@click="$emit('print', m)
							">
                                <Printer class="h-4 w-4 xl:h-5 xl:w-5" />
                            </IconButton>

							<IconButton title="Edit" variant="primary" @click="$emit('edit', m)">
								<EditIcon class="h-4 w-4 xl:h-5 xl:w-5" />
							</IconButton>

							<IconButton
								:title="m.status === 'active' ? 'Deactivate' : 'Activate'"
								:variant="m.status === 'active' ? 'warning' : 'success'"
								@click="$emit('toggleStatus', m)"
							>
								<DeactivateIcon v-if="m.status === 'active'" class="h-4 w-4 xl:h-5 xl:w-5" />
								<ActivateIcon v-else class="h-4 w-4 xl:h-5 xl:w-5" />
							</IconButton>

							<IconButton v-if="m.status != 'active'" title="Delete" variant="danger" @click="$emit('delete', m)">
								<DeleteIcon class="h-4 w-4 xl:h-5 xl:w-5" />
							</IconButton>
							</div>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<!-- Mobile Card View -->
		<div class="lg:hidden divide-y divide-slate-200/50 dark:divide-slate-700/50">
			<!-- Error State -->
			<div v-if="error" class="p-4 text-center text-red-600 dark:text-red-400">
			{{ error }}
			</div>

            <div v-else-if="loading" class="p-8 flex justify-center">
                <Loader2 class="w-8 h-8 animate-spin text-primary" />
            </div>

			<!-- Mobile Cards -->
			<div v-else-if="materials.length > 0" v-for="m in materials" :key="m.id" class="p-4">
				<div class="flex justify-between items-start mb-2">
					<span class="font-semibold text-slate-700 dark:text-slate-300">
					{{ m.title }}
					</span>
					<span
					class="badge"
					:class="m.status === 'active' ? 'badge-success' : 'badge-danger'"
					>
					{{ m.status === 'active' ? 'Active' : 'Inactive' }}
					</span>
				</div>
				<div class="mb-1">
					<span class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-lg"
						:class="m.type === 'Doc File' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400' : m.type === 'Online form' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400' : m.type === 'URL Link' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400' : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'"
					>
						{{ m.type }}
						<span v-if="m.type === 'Doc File'" class="group relative inline-flex ml-1">
							<Info class="h-3 w-3 text-amber-600 cursor-help" />
							<span class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-max max-w-[14rem] bg-slate-800 text-white dark:text-white text-xs rounded px-2 py-1 shadow-lg whitespace-normal text-center z-[100]">
								Download the document, fill it out, then click Submit to upload your completed file.
								<span class="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800"></span>
							</span>
						</span>
					</span>
				</div>
				<div class="text-sm text-slate-500 dark:text-slate-400 mb-1">
					<div class="flex items-center gap-2">
						<span>Viewers:</span>
						<span class="truncate">
							<span v-for="(viewer, index) in m.position_viewers" :key="'pos-'+viewer.id">
						  {{ viewer.name }}{{ index < (m.position_viewers?.length || 0) - 1 || (m.user_viewers?.length || 0) > 0 ? ', ' : '' }}
						</span>
							<span v-for="(viewer, index) in m.user_viewers" :key="'usr-'+viewer.id">
						  {{ viewer.first_name ? viewer.first_name + ' ' + viewer.last_name : viewer.username }}{{ index < (m.user_viewers?.length || 0) - 1 ? ', ' : '' }}
						</span>
							<span v-if="(!m.position_viewers || m.position_viewers.length === 0) && (!m.user_viewers || m.user_viewers.length === 0)" class="italic">None</span>
						</span>
						<button
							type="button"
							@click.stop="$emit('editViewers', m)"
							class="flex-shrink-0 p-1 rounded-lg text-slate-400 hover:text-primary hover:bg-primary/10 dark:hover:bg-primary-dark/10 transition-all"
							title="Edit Viewers"
						>
							<UserPen class="w-4 h-4" />
						</button>
					</div>
				</div>
				<div class="text-sm text-slate-500 dark:text-slate-400 mb-3">
					Added: {{ m.created_at ? new Date(m.created_at).toLocaleDateString() : 'N/A' }}
				</div>
				<div class="flex gap-2 flex-wrap">
					<IconButton
					title="View"
					variant="neutral"
					@click="$emit('view', m)"
					>
						<ViewIcon class="w-4 h-4 text-blue-600" />
					</IconButton>

					<IconButton
					v-if="m.type === 'Doc File' && m.file_path"
					title="Download"
					variant="neutral"
					@click="$emit('download', m)"
					>
						<DownloadIcon class="w-4 h-4" />
					</IconButton>

                    <IconButton title="Print" variant="neutral" @click="$emit('print', m)">
                        <Printer class="w-4 h-4" />
                    </IconButton>

					<IconButton title="Edit" variant="primary" @click="$emit('edit', m)">
						<EditIcon class="w-4 h-4" />
					</IconButton>

					<IconButton
					:title="m.status === 'active' ? 'Deactivate' : 'Activate'"
					:variant="m.status === 'active' ? 'warning' : 'success'"
					@click="$emit('toggleStatus', m)"
					>
						<DeactivateIcon v-if="m.status === 'active'" class="w-4 h-4" />
						<ActivateIcon v-else class="w-4 h-4" />
					</IconButton>

					<IconButton title="Delete" variant="danger" @click="$emit('delete', m)">
						<DeleteIcon class="w-4 h-4" />
					</IconButton>
				</div>
			</div>

			<!-- Mobile Empty State -->
			<div v-else class="text-center py-16 px-4">
				<div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 mb-4">
					<FileText class="w-8 h-8 text-slate-400 dark:text-slate-500" />
				</div>
				<p class="text-base font-medium text-slate-600 dark:text-slate-300">No records found</p>
				<p v-if="totalUnfiltered && totalUnfiltered > 0" class="text-sm text-slate-500 dark:text-slate-400 mt-1">Try adjusting your search or filters</p>
			</div>
		</div>
	</CardTable>
</template>

<script setup lang="ts">
import {
	FileText, Printer, Loader2, Eye as ViewIcon,
	SquarePen as EditIcon, Trash as DeleteIcon,
	LockOpen as ActivateIcon, Lock as DeactivateIcon,
	CloudDownload as DownloadIcon, UserPen, Info
} from 'lucide-vue-next'
import CardTable from '@/components/layout/CardTable.vue'
import IconButton from '@/components/ui/IconButton.vue'
import TableLoader from '@/components/ui/TableLoader.vue'

import type { Material } from '@/config/types/material'

defineProps<{
  materials: Material[]
  loading?: boolean
  error?: string | null
  title?: string
  loadingMessage?: string
  totalUnfiltered?: number
}>()

defineEmits<{
  (e: 'view', material: Material): void
  (e: 'download', material: Material): void
  (e: 'edit', material: Material): void
  (e: 'toggleStatus', material: Material): void
  (e: 'delete', material: Material): void
  (e: 'print', material: Material): void
  (e: 'editViewers', material: Material): void
}>()
</script>
